/**
 * Data Migration Dialog - Copy/migrate datasets between scenarios with progress tracking
 * Provides bulk data operations and dataset management utilities
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Storage as DatasetIcon,
  LocationOn as LocationIcon,
  DirectionsCar as VehicleIcon,
  Work as JobIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Speed as VroomIcon,
} from '@mui/icons-material';
import { apiClient } from '../../services/api';
import type { Dataset, Scenario } from '../../types/vrp';

interface DataMigrationDialogProps {
  open: boolean;
  onClose: () => void;
  sourceDataset?: Dataset;
  onSuccess?: (newDataset: Dataset) => void;
}

interface MigrationOptions {
  targetScenarioId: number;
  newDatasetName: string;
  includeLocations: boolean;
  includeVehicles: boolean;
  includeJobs: boolean;
  preserveIds: boolean;
  updateReferences: boolean;
}

interface MigrationStep {
  id: string;
  label: string;
  description: string;
  completed: boolean;
  error?: string;
  progress?: number;
}

const MIGRATION_STEPS: Omit<MigrationStep, 'completed' | 'error' | 'progress'>[] = [
  {
    id: 'validate',
    label: 'Validate Source Data',
    description: 'Check data integrity and VROOM compatibility'
  },
  {
    id: 'create-dataset',
    label: 'Create Target Dataset',
    description: 'Create new dataset in target scenario'
  },
  {
    id: 'copy-locations',
    label: 'Copy Locations',
    description: 'Transfer location data with coordinates'
  },
  {
    id: 'copy-vehicles',
    label: 'Copy Vehicles',
    description: 'Transfer vehicle data and update references'
  },
  {
    id: 'copy-jobs',
    label: 'Copy Jobs',
    description: 'Transfer job data and update location references'
  },
  {
    id: 'finalize',
    label: 'Finalize Migration',
    description: 'Update dataset statistics and verify integrity'
  }
];

export const DataMigrationDialog: React.FC<DataMigrationDialogProps> = ({
  open,
  onClose,
  sourceDataset,
  onSuccess
}) => {
  const { currentProject } = useProject();
  // Unified VRP architecture - get scenarios and datasets
  // TODO: Replace with Toolpad navigation
  const scenarios = [];
  
  const [activeStep, setActiveStep] = useState(0);
  const [migrationSteps, setMigrationSteps] = useState<MigrationStep[]>(
    MIGRATION_STEPS.map(step => ({ ...step, completed: false }))
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [options, setOptions] = useState<MigrationOptions>({
    targetScenarioId: 0,
    newDatasetName: '',
    includeLocations: true,
    includeVehicles: true,
    includeJobs: true,
    preserveIds: false,
    updateReferences: true,
  });
  const [migrationResult, setMigrationResult] = useState<Dataset | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  // Available scenarios for migration (excluding current scenario)
  const availableScenarios = scenarios.filter(s => 
    s.id !== sourceDataset?.scenario_id && s.status === 'active'
  );

  // Reset dialog state when opening
  useEffect(() => {
    if (open && sourceDataset) {
      setActiveStep(0);
      setMigrationSteps(MIGRATION_STEPS.map(step => ({ ...step, completed: false })));
      setIsProcessing(false);
      setErrors([]);
      setMigrationResult(null);
      setOptions(prev => ({
        ...prev,
        newDatasetName: `${sourceDataset.name} - Copy`,
        targetScenarioId: availableScenarios[0]?.id || 0
      }));
    }
  }, [open, sourceDataset, availableScenarios]);

  const updateStepStatus = (stepId: string, updates: Partial<MigrationStep>) => {
    setMigrationSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, ...updates } : step
    ));
  };

  const performMigration = async () => {
    if (!sourceDataset || !currentProject) return;

    setIsProcessing(true);
    setErrors([]);
    
    try {
      // Step 1: Validate source data
      setActiveStep(0);
      updateStepStatus('validate', { progress: 50 });
      
      // Fetch source data to validate
      const [locations, vehicles, jobs] = await Promise.all([
        options.includeLocations ? apiClient.getLocations(currentProject.id, sourceDataset.id) : [],
        options.includeVehicles ? apiClient.getVehicles(currentProject.id, sourceDataset.id) : [],
        options.includeJobs ? apiClient.getJobs(currentProject.id, sourceDataset.id) : []
      ]);

      updateStepStatus('validate', { completed: true });

      // Step 2: Create target dataset
      setActiveStep(1);
      updateStepStatus('create-dataset', { progress: 50 });
      
      const newDataset = await apiClient.createDataset({
        project_id: currentProject.id,
        scenario_id: options.targetScenarioId,
        name: options.newDatasetName,
        description: `Migrated from dataset: ${sourceDataset.name}`,
        dataset_type: sourceDataset.dataset_type || 'complete',
        is_default: false,
        status: 'active'
      });

      updateStepStatus('create-dataset', { completed: true });
      setMigrationResult(newDataset);

      // Step 3: Copy locations
      if (options.includeLocations && locations.length > 0) {
        setActiveStep(2);
        updateStepStatus('copy-locations', { progress: 0 });
        
        for (let i = 0; i < locations.length; i++) {
          const location = locations[i];
          const locationData = {
            ...location,
            project_id: currentProject.id,
            scenario_id: options.targetScenarioId,
            dataset_id: newDataset.id,
            id: options.preserveIds ? location.id : undefined
          };
          delete locationData.created_at;
          delete locationData.updated_at;
          
          await apiClient.createLocation(locationData);
          updateStepStatus('copy-locations', { progress: ((i + 1) / locations.length) * 100 });
        }
        
        updateStepStatus('copy-locations', { completed: true });
      } else {
        updateStepStatus('copy-locations', { completed: true });
      }

      // Step 4: Copy vehicles
      if (options.includeVehicles && vehicles.length > 0) {
        setActiveStep(3);
        updateStepStatus('copy-vehicles', { progress: 0 });
        
        for (let i = 0; i < vehicles.length; i++) {
          const vehicle = vehicles[i];
          const vehicleData = {
            ...vehicle,
            project_id: currentProject.id,
            scenario_id: options.targetScenarioId,
            dataset_id: newDataset.id,
            id: options.preserveIds ? vehicle.id : undefined
          };
          delete vehicleData.created_at;
          delete vehicleData.updated_at;
          
          await apiClient.createVehicle(vehicleData);
          updateStepStatus('copy-vehicles', { progress: ((i + 1) / vehicles.length) * 100 });
        }
        
        updateStepStatus('copy-vehicles', { completed: true });
      } else {
        updateStepStatus('copy-vehicles', { completed: true });
      }

      // Step 5: Copy jobs
      if (options.includeJobs && jobs.length > 0) {
        setActiveStep(4);
        updateStepStatus('copy-jobs', { progress: 0 });
        
        for (let i = 0; i < jobs.length; i++) {
          const job = jobs[i];
          const jobData = {
            ...job,
            project_id: currentProject.id,
            scenario_id: options.targetScenarioId,
            dataset_id: newDataset.id,
            id: options.preserveIds ? job.id : undefined
          };
          delete jobData.created_at;
          delete jobData.updated_at;
          
          await apiClient.createJob(jobData);
          updateStepStatus('copy-jobs', { progress: ((i + 1) / jobs.length) * 100 });
        }
        
        updateStepStatus('copy-jobs', { completed: true });
      } else {
        updateStepStatus('copy-jobs', { completed: true });
      }

      // Step 6: Finalize
      setActiveStep(5);
      updateStepStatus('finalize', { progress: 50 });
      
      // Refresh dataset statistics
      await apiClient.refreshDatasetStatistics(newDataset.id, newDataset.name, newDataset.scenario_id);
      
      updateStepStatus('finalize', { completed: true, progress: 100 });
      
      // Move to completion
      setActiveStep(6);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setErrors(prev => [...prev, errorMessage]);
      updateStepStatus(MIGRATION_STEPS[activeStep]?.id, { 
        error: errorMessage,
        completed: false 
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (migrationResult && onSuccess) {
      onSuccess(migrationResult);
    }
    onClose();
  };

  const canStartMigration = options.targetScenarioId > 0 && 
                            options.newDatasetName.trim().length > 0 &&
                            (options.includeLocations || options.includeVehicles || options.includeJobs);

  if (!sourceDataset) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CopyIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            Migrate Dataset
          </Typography>
          <Chip
            label={sourceDataset.name}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {!isProcessing && activeStep < 6 && (
          <Box>
            {/* Migration Options */}
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Migration Settings
            </Typography>
            
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Target Scenario</InputLabel>
                <Select
                  value={options.targetScenarioId}
                  onChange={(e) => setOptions(prev => ({ ...prev, targetScenarioId: Number(e.target.value) }))}
                  label="Target Scenario"
                >
                  {availableScenarios.map(scenario => (
                    <MenuItem key={scenario.id} value={scenario.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{scenario.name}</Typography>
                        <Chip label={scenario.status} size="small" variant="outlined" />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="New Dataset Name"
                value={options.newDatasetName}
                onChange={(e) => setOptions(prev => ({ ...prev, newDatasetName: e.target.value }))}
                placeholder="Enter name for the copied dataset"
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Data to Include
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.includeLocations}
                        onChange={(e) => setOptions(prev => ({ ...prev, includeLocations: e.target.checked }))}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" />
                        <Typography>Locations ({sourceDataset.entity_counts?.locations || 0})</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.includeVehicles}
                        onChange={(e) => setOptions(prev => ({ ...prev, includeVehicles: e.target.checked }))}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VehicleIcon fontSize="small" />
                        <Typography>Vehicles ({sourceDataset.entity_counts?.vehicles || 0})</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.includeJobs}
                        onChange={(e) => setOptions(prev => ({ ...prev, includeJobs: e.target.checked }))}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <JobIcon fontSize="small" />
                        <Typography>Jobs ({sourceDataset.entity_counts?.jobs || 0})</Typography>
                      </Box>
                    }
                  />
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Advanced Options
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={options.preserveIds}
                        onChange={(e) => setOptions(prev => ({ ...prev, preserveIds: e.target.checked }))}
                      />
                    }
                    label="Preserve Original IDs"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={options.updateReferences}
                        onChange={(e) => setOptions(prev => ({ ...prev, updateReferences: e.target.checked }))}
                      />
                    }
                    label="Update Cross-References"
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Migration Progress */}
        {(isProcessing || activeStep >= 6) && (
          <Box>
            <Stepper activeStep={activeStep} orientation="vertical">
              {migrationSteps.map((step) => (
                <Step key={step.id}>
                  <StepLabel
                    error={!!step.error}
                    StepIconComponent={() => (
                      step.error ? <ErrorIcon color="error" /> :
                      step.completed ? <SuccessIcon color="success" /> :
                      <InfoIcon color="primary" />
                    )}
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                    {step.progress !== undefined && step.progress < 100 && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress variant="determinate" value={step.progress} />
                      </Box>
                    )}
                    {step.error && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {step.error}
                      </Alert>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            {/* Migration Complete */}
            {activeStep >= 6 && migrationResult && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Migration Completed Successfully!
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Dataset "{migrationResult.name}" has been created in the target scenario.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Chip 
                      icon={<DatasetIcon />}
                      label={`Dataset ID: ${migrationResult.id}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip 
                      icon={<VroomIcon />}
                      label="Ready for Optimization"
                      size="small"
                      color="success"
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="600">
                  Migration Errors:
                </Typography>
                <List dense>
                  {errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose}>
          {migrationResult ? 'Done' : 'Cancel'}
        </Button>
        {!isProcessing && activeStep < 6 && (
          <Button
            variant="contained"
            onClick={performMigration}
            disabled={!canStartMigration}
            startIcon={<CopyIcon />}
          >
            Start Migration
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DataMigrationDialog;